import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/feature/MangaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { mangas, categories, siteInfo } from "@/mocks/manga";

export default function Category() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [activeSub, setActiveSub] = useState("all");
  const [sortBy, setSortBy] = useState<"rating" | "update" | "views">("rating");

  const currentCategory = categories.find((c) => c.slug === categorySlug);
  const categoryName = currentCategory?.name || "全部";

  useEffect(() => {
    document.title = `${categoryName} - 九妖漫画网 | 免费漫画全集在线免费下拉式阅读`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", `九妖漫画网${categoryName}专区，提供免费${categoryName}漫画全集，免费下拉式追更，好看的${categoryName}推荐，免费在线阅读。`);
    }
    setActiveSub("all");
  }, [categorySlug, categoryName]);

  const subCategories = useMemo(() => {
    const mainCategories = ["guoman", "riman", "hanman"];
    if (categorySlug && mainCategories.includes(categorySlug)) {
      return categories.filter((c) => {
        const fromMain = mangas.filter((m) => m.category === categorySlug);
        return fromMain.some((m) => m.subcategory === c.slug);
      });
    }
    return [];
  }, [categorySlug]);

  const filteredMangas = useMemo(() => {
    let result = mangas;

    if (categorySlug && categorySlug !== "all") {
      const mainCats = ["guoman", "riman", "hanman"];
      if (mainCats.includes(categorySlug)) {
        result = result.filter((m) => m.category === categorySlug);
        if (activeSub !== "all") {
          result = result.filter((m) => m.subcategory === activeSub);
        }
      } else {
        result = result.filter(
          (m) => m.category === categorySlug || m.subcategory === categorySlug
        );
        if (activeSub !== "all") {
          result = result.filter((m) => m.subcategory === activeSub);
        }
      }
    }

    if (sortBy === "rating") {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "views") {
      result = [...result].sort((a, b) => {
        const va = parseFloat(a.views.replace("亿", "0000").replace("万", ""));
        const vb = parseFloat(b.views.replace("亿", "0000").replace("万", ""));
        return vb - va;
      });
    } else {
      result = [...result].sort((a, b) => {
        if (a.updateTime.includes("今天") && !b.updateTime.includes("今天")) return -1;
        if (!a.updateTime.includes("今天") && b.updateTime.includes("今天")) return 1;
        return 0;
      });
    }

    return result;
  }, [categorySlug, activeSub, sortBy]);

  const breadcrumbItems = [
    { name: "首页", path: "/" },
    { name: categoryName },
  ];

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="px-4 md:px-6 py-6">
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共找到 {filteredMangas.length} 部漫画作品
              </p>
            </div>

            {subCategories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <button
                  onClick={() => setActiveSub("all")}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSub === "all"
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500"
                  }`}
                >
                  全部
                </button>
                {subCategories.map((sub) => (
                  <button
                    key={sub.slug}
                    onClick={() => setActiveSub(sub.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSub === sub.slug
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500"
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: "rating" as const, label: "评分最高" },
                { key: "update" as const, label: "最近更新" },
                { key: "views" as const, label: "人气最高" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? "bg-secondary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {filteredMangas.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredMangas.map((manga) => (
                  <MangaCard key={manga.id} {...manga} tags={manga.tags} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block" />
                <p className="text-foreground-500">暂无该分类的漫画</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  查看全部漫画
                </Link>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}